import { useContext, useEffect, useMemo, useState } from 'react'
import moment from 'moment';
import { ReportsContext } from '../../../context/report/ReportContext';
import Paginator from '../../../components/ui/Paginator';
import ReportDateRange from '../../../components/ui/ReportDateRange';
import Message from '../../../components/ui/Message';
import { downloadCsv } from '../../../utils/csv';

const numberFormatter = new Intl.NumberFormat('en-US', {
    style: 'decimal',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

const formatNumber = (value) => numberFormatter.format(Number(value ?? 0));
const formatDate = (value) => value ? moment(value).format('DD/MM/YYYY') : '-';

// Color del estado según su texto (los valores vienen del API)
const statusBadge = (status?: string | null) => {
    const value = String(status ?? '').toLowerCase();
    if (!value) return 'bg-light text-muted border';
    if (/(pagad|complet|aprob|exit|proces)/.test(value)) return 'bg-success-subtle text-success-emphasis';
    if (/(pend|espera|curso)/.test(value)) return 'bg-warning-subtle text-warning-emphasis';
    if (/(rechaz|error|fall|cancel)/.test(value)) return 'bg-danger-subtle text-danger-emphasis';
    return 'bg-secondary-subtle text-secondary-emphasis';
};

export const MerchantPaymentReport = () => {
    const [buscar, setBuscar] = useState('');
    const [pageActual, setPageActual] = useState<number>(1);
    const { pagos, totalPage, loadMerchantPaymentReport } = useContext(ReportsContext);

    const [dateIni, setDateIni] = useState(moment().format("YYYYMMDD"));
    const [dateEnd, setDateEnd] = useState(moment().add(1, 'day').format("YYYYMMDD"));
    // Rango con el que se generó el reporte (la paginación siempre usa este)
    const [appliedRange, setAppliedRange] = useState<{ from: string; to: string } | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!appliedRange) return;
        setIsLoading(true);
        loadMerchantPaymentReport(pageActual - 1, appliedRange.from, appliedRange.to)
            .catch(() => setError('No se pudo generar el reporte, intente de nuevo.'))
            .finally(() => setIsLoading(false));
    }, [pageActual, appliedRange])

    const handleFilter = () => {
        setAppliedRange({ from: dateIni, to: dateEnd });
        setPageActual(1);
    }

    const handleCallback = (start, end) => {
        setDateIni(start.format('YYYYMMDD'));
        setDateEnd(moment(end).add(1, 'day').format("YYYYMMDD"))
    }

    const handleDownload = () => {
        downloadCsv('ReportePagoComercios.csv', pagos.map((pago) => ({
            "Empresa": pago.EMPRESA,
            "Cant. Pagos": pago.CANTIDAD_PAGOS,
            "Pagado (USD)": formatNumber(pago.PAGADODOLARES),
            "Comisión (USD)": formatNumber(pago.PAGADO_COMISIONDOLARES),
            "Adelanto Renta (USD)": formatNumber(pago.PAGADO_ADELANTORENTADOLARES),
            "Total (USD)": formatNumber(pago.TOTALPAGODOLARES),
            "Pagado (CRC)": formatNumber(pago.PAGADOLOCAL),
            "Comisión (CRC)": formatNumber(pago.PAGADO_COMISIONLOCAL),
            "Adelanto Renta (CRC)": formatNumber(pago.PAGADO_ADELANTORENTALOCAL),
            "Total (CRC)": formatNumber(pago.TOTALPAGOLOCAL),
            "Fecha": formatDate(pago.FECHA_CREACION),
            "Estado": pago.ESTADO,
            "Banco": pago.BANCO,
            "Cuenta": pago.CUENTA_IBAN,
            "Moneda": pago.MONEDA,
            "Batch Id": pago.BATCHID,
            "Estado Batch Id": pago.ESTADO_BATCHID,
        })));
    }

    const filteredData = useMemo(() => {
        const term = buscar.trim().toLowerCase();
        if (!term) return pagos;
        return pagos.filter((pago) =>
            [pago.EMPRESA, pago.CUENTA_IBAN, pago.BANCO, pago.BATCHID]
                .some(val => String(val ?? '').toLowerCase().includes(term))
        );
    }, [pagos, buscar]);

    const renderBody = () => {
        if (isLoading) {
            return (
                <tr><td colSpan={17} className='text-center py-4'>
                    <span className="spinner-border spinner-border-sm me-2" /> Generando reporte...
                </td></tr>
            );
        }
        if (!appliedRange) {
            return (
                <tr><td colSpan={17} className='text-center py-4 text-muted'>
                    <i className="bi bi-funnel me-2" />Seleccione un rango de fechas y presione "Generar reporte".
                </td></tr>
            );
        }
        if (filteredData.length === 0) {
            return (
                <tr><td colSpan={17} className='text-center py-4 text-muted'>
                    {buscar ? 'No se encontraron pagos para la búsqueda.' : 'No existen pagos para el rango seleccionado.'}
                </td></tr>
            );
        }
        return filteredData.map(pago => (
            <tr key={pago.ID}>
                <td className="fw-medium" style={styles.nowrap}>{pago.EMPRESA}</td>
                <td className="text-center">{pago.CANTIDAD_PAGOS}</td>
                <td className="text-end" style={styles.nowrap}>{formatNumber(pago.PAGADODOLARES)}</td>
                <td className="text-end" style={styles.nowrap}>{formatNumber(pago.PAGADO_COMISIONDOLARES)}</td>
                <td className="text-end" style={styles.nowrap}>{formatNumber(pago.PAGADO_ADELANTORENTADOLARES)}</td>
                <td className="text-end fw-semibold" style={styles.nowrap}>{formatNumber(pago.TOTALPAGODOLARES)}</td>
                <td className="text-end" style={styles.nowrap}>{formatNumber(pago.PAGADOLOCAL)}</td>
                <td className="text-end" style={styles.nowrap}>{formatNumber(pago.PAGADO_COMISIONLOCAL)}</td>
                <td className="text-end" style={styles.nowrap}>{formatNumber(pago.PAGADO_ADELANTORENTALOCAL)}</td>
                <td className="text-end fw-semibold" style={styles.nowrap}>{formatNumber(pago.TOTALPAGOLOCAL)}</td>
                <td style={styles.nowrap}>{formatDate(pago.FECHA_CREACION)}</td>
                <td><span className={`badge rounded-pill ${statusBadge(pago.ESTADO)}`}>{pago.ESTADO || '-'}</span></td>
                <td style={styles.nowrap}>{pago.BANCO || '-'}</td>
                <td className="text-muted" style={{ ...styles.nowrap, fontFamily: 'monospace' }}>{pago.CUENTA_IBAN || '-'}</td>
                <td>{pago.MONEDA || '-'}</td>
                <td className="text-muted">{pago.BATCHID ?? '-'}</td>
                <td><span className={`badge rounded-pill ${statusBadge(pago.ESTADO_BATCHID)}`}>{pago.ESTADO_BATCHID || '-'}</span></td>
            </tr>
        ));
    };

    return (
        <div className='mt-5 container-fluid px-4' style={styles.container}>

            <div className="d-flex flex-wrap justify-content-between align-items-start mb-4">
                <div>
                    <h3 className="mb-1">Reporte de pago a comercios</h3>
                    <p className="text-muted mb-0">Pagos liquidados a los comercios, con comisiones y adelanto de renta.</p>
                </div>
                <div className="d-flex gap-2 mt-2 mt-md-0">
                    <button type="button" className="btn btn-outline-secondary" onClick={handleDownload} disabled={pagos.length === 0}>
                        <i className="bi bi-download me-1" /> Descargar CSV
                    </button>
                </div>
            </div>

            {/* Filtros */}
            <div className="card border-0 shadow-sm mb-3">
                <div className="card-body">
                    <div className="row g-2 align-items-end">
                        <div className="col-md-4">
                            <label className="form-label small text-muted mb-1">Rango de fechas</label>
                            <ReportDateRange onChange={handleCallback} minDate={moment().subtract(1, 'year')} />
                        </div>
                        <div className="col-md-3 col-lg-2 d-grid">
                            <button className="btn btn-primary" onClick={handleFilter} disabled={isLoading}>
                                <i className="bi bi-bar-chart me-1" /> Generar reporte
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Resultados */}
            <div className="card border-0 shadow-sm">
                <div className="card-body">
                    {appliedRange && pagos.length > 0 && (
                        <div className="row g-2 mb-3">
                            <div className="col-md-6">
                                <div className="input-group">
                                    <span className="input-group-text bg-white"><i className="bi bi-search" /></span>
                                    <input
                                        className="form-control"
                                        id="search"
                                        type="text"
                                        placeholder="Buscar por empresa, cuenta, banco o batch..."
                                        value={buscar}
                                        onChange={(e) => setBuscar(e.target.value)}
                                    />
                                    {buscar && (
                                        <button className="btn btn-outline-secondary" type="button" title="Limpiar búsqueda" onClick={() => setBuscar('')}>
                                            <i className="bi bi-x-lg" />
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    <div className='table-responsive'>
                        <table id='merchantPaymentTable' className="table table-hover align-middle mb-0">
                            <thead>
                                <tr className="text-muted small text-uppercase">
                                    <th rowSpan={2} className="align-bottom">Empresa</th>
                                    <th rowSpan={2} className="align-bottom text-center" style={styles.nowrap}>Cant. pagos</th>
                                    <th colSpan={4} className="text-center border-bottom-0">Dólares (USD)</th>
                                    <th colSpan={4} className="text-center border-bottom-0">Colones (CRC)</th>
                                    <th rowSpan={2} className="align-bottom">Fecha</th>
                                    <th rowSpan={2} className="align-bottom">Estado</th>
                                    <th rowSpan={2} className="align-bottom">Banco</th>
                                    <th rowSpan={2} className="align-bottom">Cuenta</th>
                                    <th rowSpan={2} className="align-bottom">Moneda</th>
                                    <th rowSpan={2} className="align-bottom" style={styles.nowrap}>Batch Id</th>
                                    <th rowSpan={2} className="align-bottom" style={styles.nowrap}>Estado batch</th>
                                </tr>
                                <tr className="text-muted small text-uppercase">
                                    <th className="text-end">Pagado</th>
                                    <th className="text-end">Comisión</th>
                                    <th className="text-end" style={styles.nowrap}>Adel. renta</th>
                                    <th className="text-end">Total</th>
                                    <th className="text-end">Pagado</th>
                                    <th className="text-end">Comisión</th>
                                    <th className="text-end" style={styles.nowrap}>Adel. renta</th>
                                    <th className="text-end">Total</th>
                                </tr>
                            </thead>
                            <tbody>{renderBody()}</tbody>
                        </table>
                    </div>

                    <div className="d-flex justify-content-end align-items-center mt-3">
                        {totalPage > 1 && appliedRange ? <Paginator totalPage={totalPage} pageActual={pageActual} onChange={(actual) => setPageActual(actual)} /> : <div />}
                    </div>
                </div>
            </div>

            <div style={{ height: '60px' }}></div>

            {error.length > 0 && (
                <Message type="error" message={error} closeDialog={() => setError('')} handleOk={() => setError('')} backgroundColor="rgba(0,0,0,0.4)" />
            )}
        </div>
    )
}


const styles = {
    container: {
        flex: 1,
        marginTop: 10,
    },
    nowrap: {
        whiteSpace: 'nowrap' as const,
    },
};
